import { dateByAddingDays, dateFromDateAndTimeIndex, dateIndexFromDate } from '@/services/time-utils';
import { DashboardAccountDayStats, DashboardAccountRevenue } from '@/grpc-gen/dashboard_service_pb';

export interface DailySeries {
  guestsSeries: number[];
  reservationsSeries: number[];
  noShowsSeries: number[];
  cancellationsSeries: number[];
  ratingSumSeries: number[];
  ratingCountSeries: number[];
}

export interface CurrencySeries {
  series: number[];
  paymentCount: number;
}

const MAX_AXIS_DAYS = 366;

export function buildDayIndexAxis(beginDate: number, endDate: number): number[] {
  const axis: number[] = [];
  let date = dateFromDateAndTimeIndex(beginDate);
  if (!date || beginDate > endDate) return axis;

  let di = beginDate;
  while (di <= endDate && axis.length < MAX_AXIS_DAYS) {
    axis.push(di);
    date = dateByAddingDays(date, 1);
    di = dateIndexFromDate(date);
  }
  return axis;
}

export function emptyDailySeries(length: number): DailySeries {
  return {
    guestsSeries: new Array(length).fill(0),
    reservationsSeries: new Array(length).fill(0),
    noShowsSeries: new Array(length).fill(0),
    cancellationsSeries: new Array(length).fill(0),
    ratingSumSeries: new Array(length).fill(0),
    ratingCountSeries: new Array(length).fill(0),
  };
}

export function fillDailySeries(days: DashboardAccountDayStats[], axis: number[]): DailySeries {
  const series = emptyDailySeries(axis.length);
  const indexByDate = new Map(axis.map((date, i) => [date, i]));

  days.forEach((day) => {
    const i = indexByDate.get(day.date);
    if (i === undefined) return;
    series.guestsSeries[i] = day.guests;
    series.reservationsSeries[i] = day.reservations;
    series.noShowsSeries[i] = day.noShows;
    series.cancellationsSeries[i] = day.cancellations;
    series.ratingSumSeries[i] = day.ratingSum;
    series.ratingCountSeries[i] = day.ratingCount;
  });
  return series;
}

export function revenueAmount(r: DashboardAccountRevenue): number {
  // int64 pb fields arrive as bigint; amounts are hundredths of the major unit
  return Number(r.depositRevenue + r.ccgRevenue + r.giftcardRevenue) / 100;
}

// most payments wins; alphabetical tie-break keeps the pick stable
export function pickDominantCurrency(revenues: DashboardAccountRevenue[]): string {
  const counts = new Map<string, number>();
  revenues.forEach((r) => {
    counts.set(r.currencyCode, (counts.get(r.currencyCode) ?? 0) + r.paymentCount);
  });
  const entries = Array.from(counts.entries());
  entries.sort((a, b) => b[1] - a[1] || (a[0] < b[0] ? -1 : 1));
  return entries.length ? entries[0][0] : '';
}

export function fillRevenueSeries(
  revenues: DashboardAccountRevenue[],
  axis: number[],
  currencyCode: string,
): number[] {
  const series: number[] = new Array(axis.length).fill(0);
  const indexByDate = new Map(axis.map((date, i) => [date, i]));
  revenues.forEach((r) => {
    if (r.currencyCode !== currencyCode) return;
    const i = indexByDate.get(r.date);
    if (i === undefined) return;
    series[i] += revenueAmount(r);
  });
  return series;
}

export function accumulateCurrencySeries(
  revenues: DashboardAccountRevenue[],
  axis: number[],
  into: Map<string, CurrencySeries>,
): void {
  const indexByDate = new Map(axis.map((date, i) => [date, i]));
  revenues.forEach((r) => {
    const i = indexByDate.get(r.date);
    if (i === undefined) return;
    let entry = into.get(r.currencyCode);
    if (!entry) {
      entry = { series: new Array(axis.length).fill(0), paymentCount: 0 };
      into.set(r.currencyCode, entry);
    }
    entry.series[i] += revenueAmount(r);
    entry.paymentCount += r.paymentCount;
  });
}
